/**
 * Idle lock (BUILD-PLAN 2.6): the session stays open but nothing behind it
 * is shown until the same person unlocks with their PIN (or password).
 * Unlocking resumes the same screen; "Sign out" ends the session.
 */
import type { SessionPeek } from '@velora/contracts/identity';
import { Button, Field, Input } from '@velora/ui';
import { AlertCircle, Lock } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { attemptsLeft, errorMessage, identityApi, refusalReason } from '../api.js';
import { PinPad } from './inputs.js';
import { AuthFrame } from './SignIn.js';

export interface LockScreenProps {
  peek: SessionPeek;
  onUnlocked: () => void;
  onSignedOut: () => void;
}

export function LockScreen({ peek, onUnlocked, onSignedOut }: LockScreenProps) {
  const [mode, setMode] = useState<'pin' | 'password'>(peek.hasPin ? 'pin' : 'password');
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = peek.displayName ?? 'this user';
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const unlock = async (body: { pin: string } | { password: string }) => {
    setBusy(true);
    setError(null);
    try {
      await identityApi.unlock(body);
      onUnlocked();
    } catch (err) {
      if (refusalReason(err) === 'revoked' || refusalReason(err) === 'no_session') {
        onSignedOut();
        return;
      }
      const left = attemptsLeft(err);
      setError(
        `${errorMessage(err)}${left !== null ? ` ${left} ${left === 1 ? 'try' : 'tries'} left before you are signed out.` : ''}`,
      );
      setPin('');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await identityApi.logout().catch(() => undefined);
    onSignedOut();
  };

  return (
    <AuthFrame>
      <div className="idn-view idn-lock">
        <span className="idn-lock__avatar" aria-hidden>
          {initials}
          <span className="idn-lock__badge">
            <Lock size={12} />
          </span>
        </span>
        <h2 className="idn-title">Screen locked</h2>
        <p className="idn-sub">
          {name}
          {peek.practiceName ? `, ${peek.practiceName}` : ''}. Your open work is kept.
        </p>
        {error ? (
          <div className="idn-alert" role="alert">
            <AlertCircle size={16} aria-hidden />
            <div>{error}</div>
          </div>
        ) : null}
        {mode === 'pin' ? (
          <>
            <p className="idn-hint idn-hint--center">Enter your PIN</p>
            <PinPad
              value={pin}
              onChange={setPin}
              onSubmit={(v) => void unlock({ pin: v })}
              invalid={Boolean(error)}
              disabled={busy}
            />
            {pin.length > 4 ? (
              <Button variant="primary" block onClick={() => void unlock({ pin })} loading={busy}>
                Unlock
              </Button>
            ) : null}
          </>
        ) : (
          <form
            className="idn-form"
            noValidate
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (password) void unlock({ password });
            }}
          >
            <Field label="Password">
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="lg"
                autoFocus
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              loading={busy}
              disabled={!password}
            >
              Unlock
            </Button>
          </form>
        )}
        <div className="idn-lock__links">
          {peek.hasPin ? (
            <button
              type="button"
              className="idn-link"
              onClick={() => {
                setMode(mode === 'pin' ? 'password' : 'pin');
                setError(null);
              }}
            >
              {mode === 'pin' ? 'Use password instead' : 'Use PIN instead'}
            </button>
          ) : null}
          <button type="button" className="idn-link idn-link--muted" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    </AuthFrame>
  );
}
